import React, { useState, useMemo } from 'react';
import { useBrokerIntegration } from '../../contexts/BrokerIntegrationContextCompat';
import { FiCheckCircle, FiAlertCircle, FiSettings, FiPlus } from 'react-icons/fi';
import { getBrokerDisplayName } from '../../utils/brokerUtils';
import ExpiredTokenCard from './ExpiredTokenCard';
import SelectedAccountIndicator from './SelectedAccountIndicator';
import AccountSelectionModal from './AccountSelectionModal';
import useStockStore from '../../stores/useStockStore';
import unifiedOAuthService from '../../services/brokers/unifiedOAuthService';

const BrokerConnectionStatus = ({ showAddButton = true, compact = false }) => {
  // Debug logging removed to prevent infinite re-renders
  const {
    loading,
    deleteBrokerIntegration,
    activeConnections,
    expiredConnections,
    hasConnections,
    hasExpiredConnections,
    normalizedIntegrations
  } = useBrokerIntegration();
  
  // Connection status debug logs removed to prevent infinite re-renders
  
  // Account Selection Modal state
  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [selectedBrokerIntegration, setSelectedBrokerIntegration] = useState(null);

  // Issue #44: start OAuth flow for a needs_reauth integration.
  // Mirrors the minimal path in BrokerIntegrationManager.jsx — no modal, no
  // sandbox toggle (users re-authenticating an existing live integration never
  // want sandbox). Backend will overwrite the integration row once OAuth
  // completes, returning status to active.
  const handleReconnect = async (integration) => {
    try {
      const oauthData = await unifiedOAuthService.getAuthorizationUrl(
        integration.broker,
        false
      );
      if (oauthData?.url) {
        window.location.href = oauthData.url;
      } else {
        alert('Failed to start reconnect flow. Please try again.');
      }
    } catch (error) {
      console.error(`Error initiating reconnect for ${integration.broker}:`, error);
      alert('Failed to start reconnect flow. Please try again.');
    }
  };

  const handleRemoveExpired = async (integrationId, brokerName) => {
    try {
      await deleteBrokerIntegration(integrationId);
      console.log(`✅ Removed expired ${brokerName} connection`);
      
      // TODO: Move engagement data cleanup to backend API - COMMENTED OUT
      // ลบ engagement data จาก tbl_engagement ทันที - DISABLED
      // console.log('🗑️ ลบ engagement data...');
      // const { collection, query, where, getDocs, writeBatch } = await import('firebase/firestore');
      // const { db, auth } = await import('../../services/firebase');
      
      // const userId = auth.currentUser?.uid;
      // if (userId) {
      //   const engagementQuery = query(
      //     collection(db, 'tbl_engagement'),
      //     where('userId', '==', userId)
      //   );
        
      //   const snapshot = await getDocs(engagementQuery);
      //   console.log('🔍 พบ engagement documents:', snapshot.size);
        
      //   if (!snapshot.empty) {
      //     const batch = writeBatch(db);
      //     snapshot.docs.forEach((doc) => {
      //       console.log('🗑️ ลบ document:', doc.id);
      //       batch.delete(doc.ref);
      //     });
          
      //     await batch.commit();
      //     console.log(`✅ ลบ engagement documents เรียบร้อย: ${snapshot.size} documents`);
      //   }
      // }
    } catch (error) {
      console.error(`❌ Failed to remove ${brokerName}:`, error);
      throw error;
    }
  };

  const { resetAllStocks } = useStockStore.getState();
  const { stocks } = useStockStore();
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [pendingDisconnectIntegration, setPendingDisconnectIntegration] = useState(null);

  const hasActiveOrders = () => {
    try {
      if (!Array.isArray(stocks)) return false;
      return stocks.some(s => (s?.type && s.type !== null) || (s?.status && s.status !== '') || (s?.value && Number(s.value) > 0));
    } catch {
      return false;
    }
  };

  // Memoized account info calculation using StandardizedBrokerIntegration
  const accountInfo = useMemo(() => {
    const integration = activeConnections[0] || normalizedIntegrations?.[0];
    if (!integration) return null;

    // Use StandardizedBrokerIntegration structure (credentials.selectedAccountId is primary)
    const accountNum = integration?.credentials?.selectedAccountId;

    // Get account name/nickname from StandardizedBrokerIntegration.accounts array
    const selectedAccount = integration?.accounts?.find(acc =>
      acc.accountId === accountNum || acc.accountNumber === accountNum
    );

    const accountName = selectedAccount?.accountNickname ||
                       selectedAccount?.accountId ||
                       integration?.metadata?.accountName;

    const userName = integration?.credentials?.username ||
                    integration?.metadata?.username ||
                    integration?.profile?.firstName ||
                    integration?.profile?.email;

    console.log('🔎 Memoized StandardizedBrokerIntegration account info:', {
      broker: integration?.broker,
      status: integration?.status,
      selectedAccountId: accountNum,
      accountName: accountName,
      username: userName,
      isConnected: integration?.credentials?.isConnected
    });

    return {
      accountNum,
      accountName,
      userName,
      integration
    };
  }, [activeConnections, normalizedIntegrations]);

  if (loading) {
    return (
      <div className="bg-gray-900 rounded-lg p-4 border border-gray-800 mb-6">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-white font-medium">Broker & Trading</h3>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/3 mb-2"></div>
          <div className="h-3 bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }



  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {hasConnections ? (
          <>
            <FiCheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-green-400 text-sm">
              {activeConnections.length} broker
              {activeConnections.length > 1 ? 's' : ''} connected
            </span>
            {hasExpiredConnections && (
              <span className="text-red-400 text-sm">
                ({expiredConnections.length} expired)
              </span>
            )}
          </>
        ) : hasExpiredConnections ? (
          <>
            <FiAlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-red-400 text-sm">
              {expiredConnections.length} broker
              {expiredConnections.length > 1 ? 's' : ''} expired
            </span>
          </>
        ) : (
          <>
            <FiAlertCircle className="w-4 h-4 text-yellow-500" />
            <span className="text-yellow-400 text-sm">No brokers connected</span>
          </>
        )}
      </div>
    );
  }

  // NOTE: Avoid direct DOM manipulation to prevent hydration/runtime errors in production

  return (
    <div className="bg-gray-900 rounded-lg p-4 border border-gray-800 mb-6">
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-white font-medium">Broker & Trading</h3>
        {hasConnections && (
          <span className="text-xs text-gray-500">
            {activeConnections.length} connected
          </span>
        )}
      </div>

      {/* Always show broker section */}
      <div className="space-y-3">
        {hasConnections ? (
          <>
            <div className="flex items-center gap-2 mb-3">
              <FiCheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-green-400 text-sm font-medium">Connected Broker</span>
            </div>

            {/* Show account details using memoized data */}
            {accountInfo && (
              <div className="text-gray-400 text-sm mb-3 space-y-1">
                {/* Show account name/nickname if available */}
                {accountInfo.accountName && (
                  <div>
                    <span className="text-gray-500">Account: </span>
                    <span className="text-gray-300 font-medium">{accountInfo.accountName}</span>
                  </div>
                )}

                {/* Show username if available */}
                {accountInfo.userName && (
                  <div>
                    <span className="text-gray-500">User: </span>
                    <span className="text-gray-300">{accountInfo.userName}</span>
                  </div>
                )}
              </div>
            )}

            {/* Selected Account Indicator */}
            <div className="mb-3">
              <SelectedAccountIndicator compact={false} />
            </div>


            {/* Disconnect Button */}
            <div className="flex flex-wrap gap-2">
              {(activeConnections.length > 0 ? activeConnections : normalizedIntegrations || []).map((integration) => {
                return (
                  <div key={integration.id} className="flex gap-2">
                    {/* Disconnect Button */}
                    <button
                      onClick={async () => {
                        // If there are active/pending orders, require confirmation first
                        if (hasActiveOrders()) {
                          setPendingDisconnectIntegration(integration);
                          setConfirmDisconnectOpen(true);
                          return;
                        }
                        try {
                          console.log('🔍 BrokerConnectionStatus disconnect button clicked for integration:', integration.id);         
                          sessionStorage.setItem('allowDashboardNoBroker', '1');          
                          await deleteBrokerIntegration(integration.id);                         
                          
                          // TODO: Move engagement data cleanup to backend API - COMMENTED OUT
                          // ลบ engagement data จาก tbl_engagement ทันที - DISABLED
                          // console.log('🗑️ ลบ engagement data...');
                          // const { collection, query, where, getDocs, writeBatch } = await import('firebase/firestore');
                          // const { db, auth } = await import('../../services/firebase');
                          
                          // const userId = auth.currentUser?.uid;
                          // if (userId) {
                          //   const engagementQuery = query(
                          //     collection(db, 'tbl_engagement'),
                          //     where('userId', '==', userId)
                          //   );
                          
                          //   const snapshot = await getDocs(engagementQuery);
                          //   console.log('🔍 พบ engagement documents:', snapshot.size);
                          
                          //   if (!snapshot.empty) {
                          //     const batch = writeBatch(db);
                          //     snapshot.docs.forEach((doc) => {
                          //       console.log('🗑️ ลบ document:', doc.id);
                          //       batch.delete(doc.ref);
                          //     });
                          
                          //     await batch.commit();
                          //     console.log(`✅ ลบ engagement documents เรียบร้อย: ${snapshot.size} documents`);
                          //   }
                          // }
                          
                          try { resetAllStocks && resetAllStocks(); } catch (e) { console.warn('⚠️ Failed to reset Strategy Center after disconnect:', e); }                         
                          console.log(`✅ Disconnected ${getBrokerDisplayName(integration.broker)}`);                                  
                        } catch (error) {
                          console.error(`❌ Failed to disconnect ${getBrokerDisplayName(integration.broker)}:`, error);                
                        }
                      }}
                      className="flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                      title={`Disconnect ${getBrokerDisplayName(integration.broker)}`}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Disconnect {getBrokerDisplayName(integration.broker)}
                    </button>
                  </div>
                );
              })}
            </div>
          
          </>
        ) : (
          <>
            {/* Show appropriate message based on whether there are expired connections */}
            {hasExpiredConnections ? (
              // If there are expired connections, show message to remove them first
              <div className="text-center py-4">
                <FiAlertCircle className="w-6 h-6 text-yellow-500 mx-auto mb-3" />
                <p className="text-gray-400 text-sm mb-2">No active broker connections</p>
                <p className="text-gray-500 text-xs">
                  Please remove or reconnect your expired connections below
                </p>
              </div>
            ) : (
              // Only show Connect button if there are NO expired connections
              <div className="text-center py-4">
                <FiAlertCircle className="w-6 h-6 text-yellow-500 mx-auto mb-3" />
                <p className="text-gray-400 text-sm mb-4">No broker account connected</p>
                
                {/* Connect Button */}
                <button
                  onClick={() => window.location.href = '/broker-connect'}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <FiPlus className="w-4 h-4" />
                  Connect Broker
                </button>
                
                <p className="text-gray-500 text-xs mt-2">
                  Connect your broker to start automated trading
                </p>
              </div>
            )}
          </>
        )}

        {/* Expired Connections Section - always show if any */}
        {hasExpiredConnections && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <FiAlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-red-400 text-sm font-medium">Expired Connections</span>
              <span className="text-xs text-gray-500">({expiredConnections.length})</span>
            </div>
            
            {expiredConnections.map((integration) => (
              <ExpiredTokenCard
                key={integration.id}
                integration={integration}
                onRemove={handleRemoveExpired}
                onReconnect={handleReconnect}
                brokerDisplayName={getBrokerDisplayName(integration.broker)}
              />
            ))}
          </div>
        )}

      </div>
      
      {/* Disconnect confirmation modal when active orders exist */}
      {confirmDisconnectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gray-900 border border-yellow-600 rounded-xl max-w-2xl w-full text-white shadow-xl">
            <div className="p-5 border-b border-gray-700">
              <h3 className="text-yellow-400 font-semibold text-lg">⚠️ Action Required</h3>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-200">
              <p>You are attempting to change your broker connection or remove your current connection while QFAA is either managing an active position or preparing to trade on your behalf.</p>
              <p>If you continue:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li>All pending trades will be canceled.</li>
                <li>QFAA will stop managing your open positions.</li>
                <li>Automated monitoring, risk controls, and exits will be disabled.</li>
                <li>Your positions will remain in your brokerage account, but you must manage them manually.</li>
              </ul>
              <p className="text-green-300">✅ To minimize risk, please close all active positions before switching brokers.</p>
            </div>
            <div className="p-5 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => { setConfirmDisconnectOpen(false); setPendingDisconnectIntegration(null); }}
                className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!pendingDisconnectIntegration) { setConfirmDisconnectOpen(false); return; }
                  try {
                    sessionStorage.setItem('allowDashboardNoBroker', '1');                
                    await deleteBrokerIntegration(pendingDisconnectIntegration.id);              
                    
                    // TODO: Move engagement data cleanup to backend API - COMMENTED OUT
                    // ลบ engagement data จาก tbl_engagement ทันที - DISABLED
                    // console.log('🗑️ ลบ engagement data...');
                    // const { collection, query, where, getDocs, writeBatch } = await import('firebase/firestore');
                    // const { db, auth } = await import('../../services/firebase');
                    
                    // const userId = auth.currentUser?.uid;
                    // if (userId) {
                    //   const engagementQuery = query(
                    //     collection(db, 'tbl_engagement'),
                    //     where('userId', '==', userId)
                    //   );
                      
                    //   const snapshot = await getDocs(engagementQuery);
                    //   console.log('🔍 พบ engagement documents:', snapshot.size);
                      
                    //   if (!snapshot.empty) {
                    //     const batch = writeBatch(db);
                    //     snapshot.docs.forEach((doc) => {
                    //       console.log('🗑️ ลบ document:', doc.id);
                    //       batch.delete(doc.ref);
                    //     });
                        
                    //     await batch.commit();
                    //     console.log(`✅ ลบ engagement documents เรียบร้อย: ${snapshot.size} documents`);
                    //   }
                    // }
                    
                    try { resetAllStocks && resetAllStocks(); } catch (e) { console.warn('⚠️ Failed to reset Strategy Center after disconnect:', e); }                               
                  } catch (e) {
                    console.error('Failed to disconnect after confirmation:', e);         
                  } finally {
                    setConfirmDisconnectOpen(false);
                    setPendingDisconnectIntegration(null);
                  }
                }}
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white"
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Selection Modal */}
      {showAccountSelection && selectedBrokerIntegration && (
        <AccountSelectionModal
          isOpen={showAccountSelection}
          onClose={() => {
            setShowAccountSelection(false);
            setSelectedBrokerIntegration(null);
          }}
          broker={selectedBrokerIntegration.broker}
          onAccountSelected={(selectedAccount) => {
            console.log('✅ Account selected:', selectedAccount);
            setShowAccountSelection(false);
            setSelectedBrokerIntegration(null);
          }}
        />
      )}

    </div>
  );
};

export default BrokerConnectionStatus;
